<template>
  <div class="p-6 max-w-screen-xl mx-auto space-y-6 fade-in rounded-xl">
    <!-- Header Section -->
    <div v-if="expeditionsStore.expeditions.length > 0" class="text-center space-y-2 flex">
      <div class="flex-1 flex flex-col items-center justify-center">
        <TentTree class="w-10 h-10 stroke-[1.5]" />
        <h1 class="text-2xl text-gray-900">{{ expeditionsStore.expeditions[0].name }}</h1>
      </div>
      <div class="w-20"></div>
    </div>

    <!-- Loading State -->
    <div v-if="statsStore.isLoading" class="flex items-center justify-center h-64">
      <LoaderCircle class="h-8 w-8 animate-spin text-gray-400" />
    </div>

    <!-- No Expedition State -->
    <div v-else-if="!userExpeditionId" class="bg-white rounded-xl shadow-sm p-8 text-center">
      <h2 class="text-xl font-medium text-gray-900 mb-2">No Study Group Found</h2>
      <p class="text-gray-500">You are not currently assigned to any study group.</p>
    </div>

    <!-- Members List -->
    <div v-else-if="sortedUsers && sortedUsers.length > 0" class="space-y-3">
      <div v-if="batchMainGuide" class="flex flex-col gap-2">
        <h2 class="text-sm text-gray-500">Main Guide</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
            <router-link
              :to="`/profile/${batchMainGuide.username}`"
              class="flex items-center p-4 space-x-4"
            >
              <!-- Avatar -->
              <div class="flex-shrink-0">
                <img
                  v-if="batchMainGuide.avatar_image"
                  :src="batchMainGuide.avatar_image"
                  :alt="`${batchMainGuide.first_name}'s avatar`"
                  class="w-12 h-12 rounded-full object-cover ring-2 ring-white ring-offset-2 ring-offset-gray-50"
                />
                <div
                  v-else
                  class="w-12 h-12 rounded-full bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center ring-2 ring-white ring-offset-2 ring-offset-gray-50"
                >
                  <UserRound class="w-6 h-6 text-indigo-300" />
                </div>
              </div>

              <!-- User Info -->
              <div class="flex-1 min-w-0">
                <h3 class="text-base font-medium text-gray-900">
                  {{ batchMainGuide.first_name }} {{ batchMainGuide.last_name }}
                </h3>
                <p class="text-sm text-gray-500">{{ batchMainGuide.email }}</p>
              </div>

              <!-- Arrow indicator -->
              <ChevronRight class="w-5 h-5 text-gray-400" />
            </router-link>
          </div>
        </div>
      </div>

      <h2 class="text-sm text-gray-500">Members</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="user in sortedUsers"
          :key="user.id"
          class="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
        >
          <router-link :to="`/profile/${user.username}`" class="flex items-center p-4 space-x-4">
            <!-- Avatar -->
            <div class="flex-shrink-0">
              <img
                v-if="user.avatar_image"
                :src="user.avatar_image"
                :alt="`${user.first_name}'s avatar`"
                class="w-12 h-12 rounded-full object-cover ring-2 ring-white ring-offset-2 ring-offset-gray-50"
              />
              <div
                v-else
                class="w-12 h-12 rounded-full bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center ring-2 ring-white ring-offset-2 ring-offset-gray-50"
              >
                <UserRound class="w-6 h-6 text-indigo-300" />
              </div>
            </div>

            <!-- User Info -->
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-medium text-gray-900">
                {{ user.first_name }} {{ user.last_name }}
              </h3>
              <p class="text-sm text-gray-500">{{ user.email }}</p>
            </div>

            <!-- Arrow indicator -->
            <ChevronRight class="w-5 h-5 text-gray-400" />
          </router-link>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white rounded-xl shadow-sm p-8 text-center">
      <GraduationCap class="mx-auto h-12 w-12 text-gray-400" />
      <h3 class="mt-2 text-sm font-medium text-gray-900">No Members Yet</h3>
      <p class="mt-1 text-sm text-gray-500">Your study group is waiting for members to join.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, watch } from 'vue'
import { useExpeditionsStore } from '@/stores/expeditions'
import { useStatsStore } from '@/stores/stats'
import {
  UserRound,
  LoaderCircle,
  ChevronRight,
  GraduationCap,
  TentTree
} from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import type { User } from '@/types/user'

const router = useRouter()
const expeditionsStore = useExpeditionsStore()
const statsStore = useStatsStore()

const batchMainGuide = computed<User | null>(() => {
  if (!expeditionsStore.expeditions.length) return null
  return expeditionsStore.expeditions[0].main_guide || null
})

function goBack() {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push('/')
}

const userExpeditionId = computed(() => {
  return expeditionsStore.latestExpeditionId
})

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  expeditionsStore.fetchExpeditions()
})

watch(userExpeditionId, (newId) => {
  if (newId) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    statsStore.fetchAdventurerStats(String(newId))
  }
})

const sortedUsers = computed(() => {
  if (!statsStore.stats?.users) return []
  return [...statsStore.stats.users].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || '')
  )
})
</script>
